import React, { useState, useEffect } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Edit,
  Tag,
  Calendar,
  MapPin,
  Eye,
  Code,
  Heart,
  File,
  Link as LinkIcon,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { format, isBefore, parseISO } from "date-fns";
import { ENTITY_NAMES } from "../constants/entities";
import SmartBackButton from "../components/ui/SmartBackButton";

interface Lab {
  id: string;
  created_at: string;
  title: string;
  description: string | null;
  order_number: number;
  learning_outcomes: string[] | null;
  resources:
    | {
        id: string;
        created_at: string;
        type: "url" | "file";
        title: string;
        url?: string | null;
        file_path?: string | null;
        file_name?: string | null;
        file_type?: string | null;
      }[]
    | null;
  series_id: string;
  mindfulness_topic: string | null;
  coding_topic: string | null;
  objective: string | null;
}

interface Series {
  id: string;
  title: string;
  description: string | null;
  code: string;
  type: string;
}

interface Run {
  id: string;
  name: string;
  day_of_week: string[];
  start_time: string;
  end_time: string;
  location: string;
  lab_dates?: { id: string; lab_number: number; date: string }[];
  active: boolean;
}

const Lab: React.FC = () => {
  const { seriesId, labId } = useParams<{ seriesId: string; labId: string }>();
  const [lab, setLab] = useState<Lab | null>(null);
  const [series, setSeries] = useState<Series | null>(null);
  const [currentRuns, setCurrentRuns] = useState<Run[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { userRole } = useAuth();

  const isAdmin = userRole === "admin";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        if (!labId || !seriesId) return;

        // Fetch lab data with resources
        const { data: labData, error: labError } = await supabase
          .from("labs")
          .select(
            `
            *,
            resources:lab_resources(*)
          `
          )
          .eq("id", labId)
          .single();

        if (labError) throw labError;
        setLab(labData);

        // Fetch series data
        const { data: seriesData, error: seriesError } = await supabase
          .from("series")
          .select("id, title, description, code, type")
          .eq("id", seriesId)
          .single();

        if (seriesError) throw seriesError;
        setSeries(seriesData);

        // Fetch current runs for this series
        const { data: runsData, error: runsError } = await supabase
          .from("runs")
          .select(
            `
            id,
            name,
            day_of_week,
            start_time,
            end_time,
            location,
            active,
            lab_dates!inner (
              id,
              lab_number,
              date
            )
          `
          )
          .eq("series_id", seriesId)
          .eq("active", true)
          .eq("lab_dates.lab_number", labData.order_number)
          .order("day_of_week", { ascending: true })
          .order("start_time", { ascending: true });

        if (runsError) throw runsError;
        setCurrentRuns(runsData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [labId, seriesId]);

  const sortedRuns = currentRuns
    .filter((run) => {
      const labDate = run.lab_dates?.find(
        (d) => d.lab_number === lab?.order_number
      );
      // Only include runs that have a future date set
      if (!labDate?.date) return false;
      return !isBefore(parseISO(labDate.date), new Date());
    })
    .sort((a, b) => {
      const dateA = parseISO(
        a.lab_dates?.find((d) => d.lab_number === lab?.order_number)?.date || ""
      );
      const dateB = parseISO(
        b.lab_dates?.find((d) => d.lab_number === lab?.order_number)?.date || ""
      );
      return dateA.getTime() - dateB.getTime();
    });

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="flex items-center mb-6">
          <div className="w-24 h-8 bg-neutral-200 rounded"></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
          <div className="w-48 h-8 bg-neutral-200 rounded mb-4"></div>
          <div className="w-full h-64 bg-neutral-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!lab) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-neutral-800 mb-4">
          {series?.type === "camp" ? "Day" : "Lab"} Not Found
        </h2>
        <p className="text-neutral-600 mb-6">
          The {series?.type === "camp" ? "day" : "lab"} you are looking for does not exist or has been removed.
        </p>
        <SmartBackButton
          fallbackPath={`/series/${seriesId}`}
          className="btn btn-primary"
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center mb-6">
        <SmartBackButton fallbackPath={`/series/${seriesId}`} />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="mb-1">
                {series?.type === "camp" ? "Day" : "Lab"} {lab.order_number} of {series?.code}
              </h1>
            </div>
            {isAdmin && (
              <div className="flex space-x-2">
                <RouterLink
                  to={`/series/${seriesId}/labs/${labId}/edit`}
                  className="btn btn-edit"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </RouterLink>
              </div>
            )}
          </div>

          <div className="space-y-2 mb-6">
            <div className="flex items-center">
              <Tag className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{lab.title}</span>
            </div>

            {lab.description && (
              <div className="flex items-start">
                <Eye className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <span className="font-medium">Description:</span>{" "}
                  <span className="text-neutral-700">{lab.description}</span>
                </div>
              </div>
            )}

            {lab.mindfulness_topic && (
              <div className="flex items-center">
                <Heart className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm">
                  <span className="font-medium">Mindfulness Topic:</span>{" "}
                  {lab.mindfulness_topic}
                </span>
              </div>
            )}

            {lab.coding_topic && (
              <div className="flex items-center">
                <Code className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-sm">
                  <span className="font-medium">Coding Topic:</span>{" "}
                  {lab.coding_topic}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Objective
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      <tr className="hover:bg-neutral-50">
                        <td className="px-6 py-4 text-sm text-neutral-900">
                          {lab.objective || "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Learning Outcomes
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {lab.learning_outcomes?.length ? (
                        lab.learning_outcomes.map((outcome, index) => (
                          <tr key={index} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 text-sm text-neutral-900">
                              {outcome}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="hover:bg-neutral-50">
                          <td className="px-6 py-4 text-sm text-neutral-500">
                            No learning outcomes defined
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {lab.resources && lab.resources.length > 0 && (
              <>
                <h2 className="mb-4">Resources</h2>
                <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                            Resource
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-200">
                        {lab.resources.map((resource, index) => (
                          <tr key={index} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              {resource.title}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              <div className="flex items-center gap-2">
                                {resource.type === "url" ? (
                                  <>
                                    <LinkIcon
                                      className="h-5 w-5 text-neutral-700 flex-shrink-0"
                                      aria-hidden="true"
                                    />
                                    {resource.url && (
                                      <a
                                        href={resource.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className=""
                                      >
                                        Open Link
                                      </a>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <File
                                      className="h-5 w-5 text-neutral-700 flex-shrink-0"
                                      aria-hidden="true"
                                    />
                                    {resource.file_path && (
                                      <a
                                        href={`${
                                          import.meta.env.VITE_SUPABASE_URL
                                        }/storage/v1/object/public/lab-resources/${
                                          resource.file_path
                                        }?download=${encodeURIComponent(
                                          resource.file_name || ""
                                        )}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className=""
                                        download={resource.file_name}
                                      >
                                        Download File
                                      </a>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            <section className="mb-8">
              <h2 className="mb-4">
                {ENTITY_NAMES.RUN}s Scheduled to Take This {series?.type === "camp" ? "Day" : "Lab"}
              </h2>
              {sortedRuns.length === 0 ? (
                <div className="text-neutral-500">
                  No {ENTITY_NAMES.RUN}s scheduled to take this {series?.type === "camp" ? "day" : "lab"}
                </div>
              ) : (
                <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          {ENTITY_NAMES.RUN}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                      {sortedRuns.map((run) => {
                        const labDate = run.lab_dates?.find(
                          (d) => d.lab_number === lab?.order_number
                        );

                        return (
                          <tr key={run.id} className="hover:bg-neutral-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              <div className="flex items-center">
                                <RouterLink
                                  to={`/runs/${run.id}`}
                                  className="text-link hover:text-hover"
                                >
                                  {ENTITY_NAMES.RUN} {run.name}
                                </RouterLink>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              {labDate?.date
                                ? format(parseISO(labDate.date), "MMM d, yyyy")
                                : "Not scheduled"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 text-neutral-500 mr-2" />
                                {formatTime(run.start_time)} -{" "}
                                {formatTime(run.end_time)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                              <div className="flex items-center">
                                <MapPin className="h-4 w-4 text-neutral-500 mr-2" />
                                {run.location || "No location"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500 text-right">
                              <div className="flex justify-end space-x-2">
                                <RouterLink
                                  to={`/runs/${run.id}`}
                                  className="icon-view"
                                  title="View run details"
                                >
                                  <Eye className="h-5 w-5" />
                                </RouterLink>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Lab;
